import type { BookingAttachment, GhlBookingStatus } from "../types/ghlBookingChat";

const KNOWN_STATUSES: GhlBookingStatus[] = [
  "confirmed",
  "not_connected",
  "needs_reauth",
  "no_calendar",
  // ghl_book_appointment resolves the calendar the same way availability does,
  // so it returns these two as well. Omitting them here collapsed both into
  // "unavailable" and lost the reason.
  "calendar_not_found",
  "calendar_ambiguous",
  "unavailable",
  "slot_taken",
  "missing_contact_info",
];

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

/**
 * Parses `data.result` string from SSE tool_call for ghl_book_appointment.
 * Only "confirmed" WITH a real confirmation_id reads as booked — a
 * "confirmed" status missing an id (an ambiguous/malformed payload) is
 * deliberately downgraded to "unavailable" rather than trusted, same rule
 * the backend itself enforces before ever returning "confirmed"
 * (ghl_calendar_client.py never reports a booking on a 2xx with no id).
 */
export function parseBookingToolResult(resultStr: string): BookingAttachment | null {
  try {
    const raw = JSON.parse(resultStr) as Record<string, unknown>;
    if (!isRecord(raw)) return null;

    const status = typeof raw.status === "string" ? raw.status : "unavailable";
    const startTime = typeof raw.start_time === "string" ? raw.start_time : "";
    const durationMinutes = typeof raw.duration_minutes === "number" ? raw.duration_minutes : 30;

    if (status === "confirmed" && typeof raw.confirmation_id === "string" && raw.confirmation_id) {
      return {
        result: {
          status: "confirmed",
          start_time: startTime,
          end_time: typeof raw.end_time === "string" ? raw.end_time : null,
          duration_minutes: durationMinutes,
          // The backend sends both; dropping them here is what left
          // AppointmentCard rendering the time in the viewer's own zone —
          // a 10:00 AM Central booking displayed as 11:00 PM under a
          // CONFIRMED badge.
          timezone: typeof raw.timezone === "string" ? raw.timezone : undefined,
          calendar_name: typeof raw.calendar_name === "string" ? raw.calendar_name : undefined,
          confirmation_id: raw.confirmation_id,
        },
      };
    }

    const safeStatus = KNOWN_STATUSES.includes(status as GhlBookingStatus) && status !== "confirmed"
      ? (status as Exclude<GhlBookingStatus, "confirmed">)
      : "unavailable";

    return {
      result: {
        status: safeStatus,
        start_time: startTime,
        duration_minutes: durationMinutes,
        message: typeof raw.message === "string" ? raw.message : undefined,
        matching_calendars: Array.isArray(raw.matching_calendars)
          ? raw.matching_calendars.filter((c): c is string => typeof c === "string")
          : undefined,
      },
    };
  } catch {
    return null;
  }
}
